import { useQuery, useMutation } from "./urqlHooks";

const COMPANY = `
query get_company($id: String!){
    Company(id:$id){
        id,
        name,
        rut,
        clientPaymentDays,
        providerPaymentDays,
        alias,
        plan,
        taxRecoverable,
        taxAdditional,
        autoPostponeF29,
        taxAdditionalAccount {
            accountKey
            accountName
        },
        payDebitNotes,
        paySalaries,
        ppmRate,
        siiBienesRaices {
          totalAvaluoFiscal
          properties
          fetchedAt
        },
        reconstructionReport {
          generatedAt
          totalAvaluoFiscal
          properties
          hasPdf
        },
        requiresAccountingReconstruction,
        accountingReconstructionStatus,
        isPascaleActive {
            active
            mode
            reportsFrom
        },
        reports{
            name,
            url
        }
  }
}
`;

export const GetCompanyData = id => {
  const [result, reexecuteQuery] = useQuery({
    query: COMPANY,
    variables: { id },
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const COMPANY_DETAIL = `
query COMPANY_DETAIL($companyId: String!) {
  Company(id: $companyId) {
    id
    softlanding {
      isCompleted
      contactStepSet
      steps {
        name
        completed
        completedAt
        fields {
          key
          value
        }
      }
    }
    taxInformation {
      codigoError
      descripcionError
      sysdate
      contribuyente {
        rut
        dv
        razonSocial
        eMail
        fechaInicioActividades
        tipoContribuyenteDescripcion
        subtipoContribuyenteDescrip
      }
      actEcos {
        codigo
        descripcion
        categoriaTributaria
        afectoIva
        fechaInicio
      }
      direcciones {
        calle
        numero
        comunaDescripcion
        regionDescripcion
        villaPoblacion
        ciudad
        correo
      }
      representantes {
        rut
        dv
        razonSocial
        nombres
        apellidoPaterno
        apellidoMaterno
        vigente
        fechaInicio
        fechaTermino
      }
      socios {
        rut
        dv
        razonSocial
        nombres
        apellidoPaterno
        apellidoMaterno
        aporteEnterado
        participacionCapital
        participacionUtilidades
        fechaIncorporacion
        fechaFinParticipacion
        vigente
      }
      timbrajes {
        codigo
        descripcion
        numeroInicial
        numeroFinal
        fechaLegalizacion
      }
      atributos {
        rut
        dv
        atrCodigo
        descAtrCodigo
        fechaInicio
        valor
        fechaTermino
        usuarioActivacion
        usuarioDesactivacion
        eliminado
        fechaCreacion
      }
      listParticipSocio {
        rutSociedad
        dvSociedad
        nombres
        apellidoPaterno
        apellidoMaterno
        razonSocial
        fechaIncorpacion
        aportePorEnterar
        fechaFinParticipacion
        sraGpk
        sraNdr
        aporteEnterado
        participacionCapital
        participacionUtilidades
        fechaModificacion
        fechaPorEnterar
        numAlertas
        estado
      }
    }
  }
}
`;

export const GetCompanyDetail = (companyId) => {
  const [result, reexecuteQuery] = useQuery({
    query: COMPANY_DETAIL,
    variables: { companyId },
    pause: !companyId,
    requestPolicy: 'cache-and-network',
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_CONSOLIDATED_CONTACTS = `
query GET_CONSOLIDATED_CONTACTS($companyId: String!) {
  getConsolidatedContacts(companyId: $companyId) {
    email
    name
    phone
    types
  }
}
`;

export const GetConsolidatedContacts = (companyId) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_CONSOLIDATED_CONTACTS,
    variables: { companyId: companyId || '' },
    pause: !companyId || companyId === null || companyId === undefined || companyId === '',
    requestPolicy: 'cache-and-network',
  });
  const { data, fetching, error } = result;
  
  if (error) {
    console.error('GetConsolidatedContacts: Error fetching consolidated contacts:', error);
  }
  
  return { data, fetching, error, reexecuteQuery };
};


const CREATE_COMPANY = `
mutation CREATE_COMPANY($input: CompanyInput!, $voucher: String) {
	createCompany(input: $input, voucher: $voucher) {
		status
		message
		data {
			id
			name
			correlative
			onboarding {
				url
				createdAt
				isCompleted
				users {
					email
					invitedAt
				}
			}
		}
	}
}`;

export const CreateCompany = () => {
  const [result, executeMutation] = useMutation(CREATE_COMPANY)
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation }
}

const NEXT_COMPANY_CORRELATIVE = `
query NEXT_COMPANY_CORRELATIVE($kind: CorrelativeKind!) {
  nextCompanyCorrelative(kind: $kind)
}`;

export { NEXT_COMPANY_CORRELATIVE };

export const NextCompanyCorrelative = (kind, pause = false) => {
  const [result, reexecuteQuery] = useQuery({
    query: NEXT_COMPANY_CORRELATIVE,
    variables: { kind: kind || 'MP' },
    pause: pause || !kind,
    requestPolicy: 'network-only',
  })
  const { data, fetching, error } = result
  return { data, fetching, error, reexecuteQuery }
}

const IS_COMPANY_CORRELATIVE_AVAILABLE = `
query IS_COMPANY_CORRELATIVE_AVAILABLE($correlative: String!) {
  isCompanyCorrelativeAvailable(correlative: $correlative)
}`;

export { IS_COMPANY_CORRELATIVE_AVAILABLE };

const UPDATE_COMPANY = `
mutation UPDATE_COMPANY($input: UpdateCompanyInput!, $id: String!) {
	updateCompany(input: $input, id: $id) {
		id
	}
}`;

export const UpdateCompany = () => {
  const [result, executeMutation] = useMutation(UPDATE_COMPANY)
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation }
}

const PAYMENT_SUCCESSFUL = `
mutation PAYMENT_SUCCESSFUL($companyId: String!, $suscriptionId: String!) {
	paymentSuccessful(companyId: $companyId, suscriptionId: $suscriptionId)
}`;

export const PaymentSuccessful = () => {
  const [result, executeMutation] = useMutation(PAYMENT_SUCCESSFUL)
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation }
}


const USER_COMPANY_ROLE = `
query getUserCompanyRole($companyId: String!){
  getUserCompanyRole(companyId:$companyId){
    role
  }
}
`;

export const GetUserCompanyRole = ({companyId}) => {
  const [result, reexecuteQuery] = useQuery({
    query: USER_COMPANY_ROLE,
    variables: { companyId },
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

export const ALL_USERS_BY_COMPANY_QUERY = `
  query AllUsersByCompany($companyId: String!) {
    allUsersByCompany(companyId:$companyId) {
        id
        name
        email
        roles
        companyRole
        adminStatus
    }
}
`;

/** Refreshes @-mention user lists everywhere this query is subscribed. */
export const refetchAllUsersByCompany = (client, companyId) => {
  if (!client?.query || !companyId) return;
  const result = client.query(ALL_USERS_BY_COMPANY_QUERY, { companyId }, {
    requestPolicy: 'network-only',
  });
  if (result && typeof result.toPromise === 'function') {
    result.toPromise().catch(() => {});
  }
};

export const GetAllUserByCompany = ({companyId}) => {
  const [result, reexecuteQuery] = useQuery({
    query: ALL_USERS_BY_COMPANY_QUERY,
    variables: { companyId },
    pause: !companyId,
    // Avoid cache-first: company user lists must be complete (e.g. teams, @mentions).
    requestPolicy: 'cache-and-network',
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const ALL_ELIGIBLE_TEAM_USERS_QUERY = `
  query AllEligibleTeamUsers {
    allEligibleTeamUsers {
      id
      name
      email
      roles
      adminStatus
    }
  }
`;

export const GetAllEligibleTeamUsers = () => {
  const [result, reexecuteQuery] = useQuery({
    query: ALL_ELIGIBLE_TEAM_USERS_QUERY,
    requestPolicy: 'cache-and-network',
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const MODIFY_COMPANY = `
mutation MODIFY_COMPANY($companyId: String!, $input: [DocumentToModify!]!) {
  modifyCompany(companyId: $companyId, input: $input) {
    ok
    nModified
  }
}`;

export const ModifyCompany = () => {
  const [result, executeMutation] = useMutation(MODIFY_COMPANY)
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation }
}

const GET_COMPANY_GROUPS = `
query GetCompanyGroups {
  getCompanyGroups
}`;

export const GetCompanyGroups = ({ pause = false } = {}) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_COMPANY_GROUPS,
    pause,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
}

const GET_COMPANY_TEAMS = `
query GET_COMPANY_TEAMS($companyId: String!) {
  getCompanyTeams(companyId: $companyId) {
    key
    name
    members {
      userId
      email
      name
      assignedAt
      assignedBy
    }
  }
}
`;

export const GetCompanyTeams = ({ companyId }) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_COMPANY_TEAMS,
    variables: { companyId },
    pause: !companyId,
    requestPolicy: 'cache-and-network',
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const ASSIGN_USER_TO_COMPANY_TEAM = `
mutation ASSIGN_USER_TO_COMPANY_TEAM($companyId: String!, $teamKey: String!, $userId: String!) {
  assignUserToCompanyTeam(companyId: $companyId, teamKey: $teamKey, userId: $userId) {
    key
    name
    members {
      userId
      email
      name
      assignedAt
      assignedBy
    }
  }
}
`;

export const AssignUserToCompanyTeam = () => {
  const [result, executeMutation] = useMutation(ASSIGN_USER_TO_COMPANY_TEAM);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const REMOVE_USER_FROM_COMPANY_TEAM = `
mutation REMOVE_USER_FROM_COMPANY_TEAM($companyId: String!, $teamKey: String!, $userId: String!) {
  removeUserFromCompanyTeam(companyId: $companyId, teamKey: $teamKey, userId: $userId) {
    key
    name
    members {
      userId
      email
      name
      assignedAt
      assignedBy
    }
  }
}
`;

export const RemoveUserFromCompanyTeam = () => {
  const [result, executeMutation] = useMutation(REMOVE_USER_FROM_COMPANY_TEAM);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};